/** @module @liquidefy/code-editor/terminal/appearance/theme @version 1.0.0 @license LQD Research LLC */

/**
 * @rust terminal_appearance_theme.rs — false; 4/2/26; sm
 *
 * Terminal ↔ Glass Theme Bridge
 *
 * Maps the glassmorphism theme system to terminal-specific concerns.
 * All ANSI colors are perceptually balanced in OKLCH with consistent
 * lightness within normal/bright groups.
 */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS & TYPES
// ═══════════════════════════════════════════════════════════════════════════════

import type { TerminalColor } from '../../code-editor-types.js';

export interface ANSIColorPalette {
  readonly black: string;
  readonly red: string;
  readonly green: string;
  readonly yellow: string;
  readonly blue: string;
  readonly magenta: string;
  readonly cyan: string;
  readonly white: string;
  readonly brightBlack: string;
  readonly brightRed: string;
  readonly brightGreen: string;
  readonly brightYellow: string;
  readonly brightBlue: string;
  readonly brightMagenta: string;
  readonly brightCyan: string;
  readonly brightWhite: string;
}

export interface TerminalGlassConfig {
  readonly bufferSurface: { blur: number; opacity: number; tintColor: string };
  readonly scrollbarSurface: { blur: number; opacity: number };
  readonly paneBorder: { opacity: number; color: string; glow: boolean };
  readonly statusLine: { blur: number; opacity: number; tintColor: string };
  readonly tabBar: { blur: number; opacity: number };
  readonly searchOverlay: { blur: number; opacity: number };
}

export interface TerminalTheme {
  readonly background: string;
  readonly foreground: string;
  readonly cursor: string;
  readonly cursorAccent: string;
  readonly selection: string;
  readonly scrollbar: string;
  readonly border: string;
  readonly ansiColors: ANSIColorPalette;
  readonly glass: TerminalGlassConfig;
  readonly dimOpacity: number;
}

export type TerminalColorScheme = 'dark' | 'light';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 INLINE COLOR HELPERS — hex ↔ rgb ↔ oklch (self-contained)
// ═══════════════════════════════════════════════════════════════════════════════

interface RGB { r: number; g: number; b: number }
interface OKLCH { L: number; C: number; H: number }

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

function hexToRGB(hex: string): RGB {
  const h = hex.replace('#', '');
  return {
    r: parseInt(h.slice(0, 2), 16) / 255,
    g: parseInt(h.slice(2, 4), 16) / 255,
    b: parseInt(h.slice(4, 6), 16) / 255,
  };
}

function rgbToHex(r: number, g: number, b: number): string {
  const to8 = (v: number) => Math.round(clamp(v, 0, 1) * 255).toString(16).padStart(2, '0');
  return `#${to8(r)}${to8(g)}${to8(b)}`;
}

function linearize(v: number): number {
  return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}

function delinearize(v: number): number {
  return v <= 0.0031308 ? v * 12.92 : 1.055 * Math.pow(v, 1 / 2.4) - 0.055;
}

function rgbToOKLCH(rgb: RGB): OKLCH {
  const r = linearize(rgb.r);
  const g = linearize(rgb.g);
  const b = linearize(rgb.b);

  const l_ = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
  const m_ = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
  const s_ = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;

  const l = Math.cbrt(l_);
  const m = Math.cbrt(m_);
  const s = Math.cbrt(s_);

  const L = 0.2104542553 * l + 0.7936177850 * m - 0.0040720468 * s;
  const a = 1.9779984951 * l - 2.4285922050 * m + 0.4505937099 * s;
  const bVal = 0.0259040371 * l + 0.7827717662 * m - 0.8086757660 * s;

  const C = Math.sqrt(a * a + bVal * bVal);
  let H = Math.atan2(bVal, a) * (180 / Math.PI);
  if (H < 0) H += 360;

  return { L, C, H };
}

function oklchToRGB(oklch: OKLCH): RGB {
  const { L, C, H } = oklch;
  const hRad = H * (Math.PI / 180);
  const a = C * Math.cos(hRad);
  const b = C * Math.sin(hRad);

  const l = L + 0.3963377774 * a + 0.2158037573 * b;
  const m = L - 0.1055613458 * a - 0.0638541728 * b;
  const s = L - 0.0894841775 * a - 1.2914855480 * b;

  const l_ = l * l * l;
  const m_ = m * m * m;
  const s_ = s * s * s;

  const r = +4.0767416621 * l_ - 3.3077115913 * m_ + 0.2309699292 * s_;
  const g = -1.2684380046 * l_ + 2.6097574011 * m_ - 0.3413193965 * s_;
  const bVal = -0.0041960863 * l_ - 0.7034186147 * m_ + 1.7076147010 * s_;

  return { r: delinearize(clamp(r, 0, 1)), g: delinearize(clamp(g, 0, 1)), b: delinearize(clamp(bVal, 0, 1)) };
}

function hexToOKLCH(hex: string): OKLCH {
  return rgbToOKLCH(hexToRGB(hex));
}

function oklchToHex(oklch: OKLCH): string {
  const { r, g, b } = oklchToRGB(oklch);
  return rgbToHex(r, g, b);
}

function lighten(hex: string, amount: number): string {
  const lch = hexToOKLCH(hex);
  return oklchToHex({ L: clamp(lch.L + amount, 0, 1), C: lch.C, H: lch.H });
}

function darken(hex: string, amount: number): string {
  return lighten(hex, -amount);
}

function setLightness(hex: string, L: number): string {
  const lch = hexToOKLCH(hex);
  return oklchToHex({ L: clamp(L, 0, 1), C: lch.C, H: lch.H });
}

function mixHex(a: string, b: string, t: number): string {
  const ra = hexToRGB(a);
  const rb = hexToRGB(b);
  return rgbToHex(
    ra.r + (rb.r - ra.r) * t,
    ra.g + (rb.g - ra.g) * t,
    ra.b + (rb.b - ra.b) * t,
  );
}

function withAlpha(hex: string, alpha: number): string {
  const a = Math.round(clamp(alpha, 0, 1) * 255).toString(16).padStart(2, '0');
  return `${hex.slice(0, 7)}${a}`;
}

function luminance(hex: string): number {
  const { r, g, b } = hexToRGB(hex);
  return 0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b);
}

function contrastRatio(fg: string, bg: string): number {
  const lFg = luminance(fg) + 0.05;
  const lBg = luminance(bg) + 0.05;
  return lFg > lBg ? lFg / lBg : lBg / lFg;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3 DEFAULT ACCENT
// ═══════════════════════════════════════════════════════════════════════════════

const DEFAULT_ACCENT = '#60a5fa';

// ═══════════════════════════════════════════════════════════════════════════════
// §4 ANSI COLOR PALETTE — perceptually balanced via OKLCH
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Generate 16 ANSI colors perceptually balanced in OKLCH.
 * Normal colors share a consistent lightness band; bright colors share a higher band.
 * Hues are fixed to semantic expectations (red~25, green~145, blue~260, etc.)
 * but chroma is derived from the accent to maintain palette harmony.
 */
export function createANSIColorPalette(scheme: TerminalColorScheme, accentHex: string): ANSIColorPalette {
  const accent = hexToOKLCH(accentHex);
  const baseChroma = clamp(accent.C * 0.85, 0.06, 0.18);

  const normalL = scheme === 'dark' ? 0.65 : 0.50;
  const brightL = scheme === 'dark' ? 0.80 : 0.62;
  const blackL  = scheme === 'dark' ? 0.25 : 0.30;
  const whiteL  = scheme === 'dark' ? 0.75 : 0.65;
  const brightBlackL  = scheme === 'dark' ? 0.45 : 0.48;
  const brightWhiteL  = scheme === 'dark' ? 0.95 : 0.88;

  const hues = { red: 25, green: 145, yellow: 85, blue: 260, magenta: 320, cyan: 195 };

  const c = (L: number, C: number, H: number) => oklchToHex({ L, C, H });

  return {
    black:         oklchToHex({ L: blackL, C: 0.005, H: accent.H }),
    red:           c(normalL, baseChroma, hues.red),
    green:         c(normalL, baseChroma, hues.green),
    yellow:        c(normalL, baseChroma, hues.yellow),
    blue:          c(normalL, baseChroma, hues.blue),
    magenta:       c(normalL, baseChroma, hues.magenta),
    cyan:          c(normalL, baseChroma, hues.cyan),
    white:         oklchToHex({ L: whiteL, C: 0.005, H: accent.H }),
    brightBlack:   oklchToHex({ L: brightBlackL, C: 0.005, H: accent.H }),
    brightRed:     c(brightL, baseChroma * 1.1, hues.red),
    brightGreen:   c(brightL, baseChroma * 1.1, hues.green),
    brightYellow:  c(brightL, baseChroma * 1.1, hues.yellow),
    brightBlue:    c(brightL, baseChroma * 1.1, hues.blue),
    brightMagenta: c(brightL, baseChroma * 1.1, hues.magenta),
    brightCyan:    c(brightL, baseChroma * 1.1, hues.cyan),
    brightWhite:   oklchToHex({ L: brightWhiteL, C: 0.003, H: accent.H }),
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §5 TERMINAL GLASS CONFIG
// ═══════════════════════════════════════════════════════════════════════════════

/** Glass surface definitions for terminal regions. */
export function createTerminalGlass(scheme: TerminalColorScheme): TerminalGlassConfig {
  const isDark = scheme === 'dark';
  const tint = isDark ? '#0a0e17' : '#f0f2f5';
  const borderColor = isDark ? '#ffffff' : '#000000';

  return {
    bufferSurface: { blur: 16, opacity: isDark ? 0.75 : 0.65, tintColor: tint },
    scrollbarSurface: { blur: 8, opacity: isDark ? 0.3 : 0.2 },
    paneBorder: { opacity: isDark ? 0.12 : 0.08, color: borderColor, glow: isDark },
    statusLine: { blur: 12, opacity: isDark ? 0.6 : 0.5, tintColor: isDark ? '#0d1117' : '#e8eaed' },
    tabBar: { blur: 12, opacity: isDark ? 0.55 : 0.45 },
    searchOverlay: { blur: 20, opacity: isDark ? 0.8 : 0.7 },
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §6 createTerminalTheme — THE main function
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Generate a full terminal theme from a color scheme and optional accent.
 * Dark scheme: deep near-black ground, soft text, OKLCH-balanced ANSI.
 * Light scheme: warm white ground, dark text, softer ANSI colors.
 */
export function createTerminalTheme(scheme: TerminalColorScheme, accentHex?: string): TerminalTheme {
  const accent = accentHex ?? DEFAULT_ACCENT;
  const isDark = scheme === 'dark';

  const background = isDark ? '#0d1117' : '#f6f8fa';
  const foreground = isDark ? '#c9d1d9' : '#24292f';
  const cursor = accent;
  const cursorAccent = isDark ? '#0d1117' : '#ffffff';
  const selection = withAlpha(accent, isDark ? 0.25 : 0.20);
  const scrollbar = withAlpha(isDark ? '#c9d1d9' : '#57606a', 0.3);
  const border = withAlpha(isDark ? '#ffffff' : '#000000', isDark ? 0.10 : 0.06);

  return {
    background,
    foreground,
    cursor,
    cursorAccent,
    selection,
    scrollbar,
    border,
    ansiColors: createANSIColorPalette(scheme, accent),
    glass: createTerminalGlass(scheme),
    dimOpacity: isDark ? 0.55 : 0.65,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §7 CSS CUSTOM PROPERTIES
// ═══════════════════════════════════════════════════════════════════════════════

/** Generate CSS custom properties from a terminal theme. */
export function terminalThemeToCSS(theme: TerminalTheme): Record<string, string> {
  const props: Record<string, string> = {
    '--terminal-bg': theme.background,
    '--terminal-fg': theme.foreground,
    '--terminal-cursor': theme.cursor,
    '--terminal-cursor-accent': theme.cursorAccent,
    '--terminal-selection': theme.selection,
    '--terminal-scrollbar': theme.scrollbar,
    '--terminal-border': theme.border,
    '--terminal-dim-opacity': String(theme.dimOpacity),
    '--ansi-black': theme.ansiColors.black,
    '--ansi-red': theme.ansiColors.red,
    '--ansi-green': theme.ansiColors.green,
    '--ansi-yellow': theme.ansiColors.yellow,
    '--ansi-blue': theme.ansiColors.blue,
    '--ansi-magenta': theme.ansiColors.magenta,
    '--ansi-cyan': theme.ansiColors.cyan,
    '--ansi-white': theme.ansiColors.white,
    '--ansi-bright-black': theme.ansiColors.brightBlack,
    '--ansi-bright-red': theme.ansiColors.brightRed,
    '--ansi-bright-green': theme.ansiColors.brightGreen,
    '--ansi-bright-yellow': theme.ansiColors.brightYellow,
    '--ansi-bright-blue': theme.ansiColors.brightBlue,
    '--ansi-bright-magenta': theme.ansiColors.brightMagenta,
    '--ansi-bright-cyan': theme.ansiColors.brightCyan,
    '--ansi-bright-white': theme.ansiColors.brightWhite,
    '--terminal-glass-blur': `${theme.glass.bufferSurface.blur}px`,
    '--terminal-glass-opacity': String(theme.glass.bufferSurface.opacity),
    '--terminal-glass-tint': theme.glass.bufferSurface.tintColor,
    '--terminal-status-blur': `${theme.glass.statusLine.blur}px`,
    '--terminal-status-opacity': String(theme.glass.statusLine.opacity),
    '--terminal-status-tint': theme.glass.statusLine.tintColor,
    '--terminal-pane-border-opacity': String(theme.glass.paneBorder.opacity),
    '--terminal-pane-border-color': theme.glass.paneBorder.color,
  };
  return props;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §8 ANSI INDEX LOOKUP
// ═══════════════════════════════════════════════════════════════════════════════

const ANSI_KEYS: ReadonlyArray<keyof ANSIColorPalette> = [
  'black', 'red', 'green', 'yellow', 'blue', 'magenta', 'cyan', 'white',
  'brightBlack', 'brightRed', 'brightGreen', 'brightYellow',
  'brightBlue', 'brightMagenta', 'brightCyan', 'brightWhite',
];

function ansiIndexToHex(index: number, palette: ANSIColorPalette): string {
  if (index >= 0 && index < 16) {
    return palette[ANSI_KEYS[index]!]!;
  }
  return '#ffffff';
}

// ═══════════════════════════════════════════════════════════════════════════════
// §9 256-COLOR TABLE
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Generate the full 256-color xterm table:
 *  0-15:   from the ANSI palette
 *  16-231: 6x6x6 color cube, lightly tinted toward the theme background
 *  232-255: grayscale ramp
 */
export function generate256ColorTable(theme: TerminalTheme): string[] {
  const table: string[] = new Array(256);

  // 0-15: ANSI palette
  for (let i = 0; i < 16; i++) {
    table[i] = ansiIndexToHex(i, theme.ansiColors);
  }

  // 16-231: 6x6x6 color cube
  const levels = [0, 0.2, 0.4, 0.6, 0.8, 1.0];
  for (let ri = 0; ri < 6; ri++) {
    for (let gi = 0; gi < 6; gi++) {
      for (let bi = 0; bi < 6; bi++) {
        const idx = 16 + ri * 36 + gi * 6 + bi;
        const raw = rgbToHex(levels[ri]!, levels[gi]!, levels[bi]!);
        // Tint 5% toward background for cohesion
        table[idx] = mixHex(raw, theme.background, 0.05);
      }
    }
  }

  // 232-255: grayscale ramp
  for (let i = 0; i < 24; i++) {
    const v = (8 + i * 10) / 255;
    const raw = rgbToHex(v, v, v);
    table[232 + i] = mixHex(raw, theme.background, 0.03);
  }

  return table;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §10 resolveTerminalColor — TerminalColor → hex
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Resolve a TerminalColor to a concrete hex value using the theme palette.
 * default → foreground, 3bit/4bit → ANSI palette, 8bit → 256 lookup,
 * 24bit → direct RGB, indexed → 256 table, theme → palette name lookup.
 */
export function resolveTerminalColor(color: TerminalColor, theme: TerminalTheme): string {
  switch (color.mode) {
    case 'default':
      return theme.foreground;

    case '3bit':
    case '4bit': {
      const idx = color.index ?? 7;
      return ansiIndexToHex(clamp(idx, 0, 15), theme.ansiColors);
    }

    case '8bit': {
      const idx = color.index ?? 7;
      const table = generate256ColorTable(theme);
      return table[clamp(idx, 0, 255)]!;
    }

    case '24bit':
      return rgbToHex(
        (color.r ?? 255) / 255,
        (color.g ?? 255) / 255,
        (color.b ?? 255) / 255,
      );

    case 'indexed': {
      const idx = color.index ?? 7;
      if (idx < 16) return ansiIndexToHex(idx, theme.ansiColors);
      const table = generate256ColorTable(theme);
      return table[clamp(idx, 0, 255)]!;
    }

    case 'theme': {
      const name = color.theme ?? 'foreground';
      return resolveThemeColorName(name, theme);
    }

    default:
      return theme.foreground;
  }
}

function resolveThemeColorName(name: string, theme: TerminalTheme): string {
  switch (name) {
    case 'foreground': return theme.foreground;
    case 'background': return theme.background;
    case 'cursor':     return theme.cursor;
    case 'selection':  return theme.selection;
    case 'red':        return theme.ansiColors.red;
    case 'green':      return theme.ansiColors.green;
    case 'yellow':     return theme.ansiColors.yellow;
    case 'blue':       return theme.ansiColors.blue;
    case 'magenta':    return theme.ansiColors.magenta;
    case 'cyan':       return theme.ansiColors.cyan;
    case 'white':      return theme.ansiColors.white;
    case 'black':      return theme.ansiColors.black;
    default:           return theme.foreground;
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// §11 GLASS BLENDING
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Compute what a color looks like when rendered on a glass surface.
 * Mixes the foreground color with the glass tint at the given opacity.
 */
export function blendWithGlass(hexColor: string, glassOpacity: number, glassTint: string): string {
  // The glass surface shows (1 - opacity) of the background through
  // and opacity of the tint. Content on top blends with this composite.
  const tintStrength = clamp(glassOpacity, 0, 1);
  return mixHex(hexColor, glassTint, tintStrength * 0.15);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §12 CONTRAST ADJUSTMENT
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Ensure terminal text meets minimum contrast against the terminal background.
 * If contrast is insufficient, nudge the foreground lightness in OKLCH.
 * Default minimum ratio: 4.5 (WCAG AA for normal text).
 */
export function adjustForContrast(fg: string, bg: string, minRatio: number = 4.5): string {
  let current = fg;
  const bgLum = luminance(bg);
  const fgIsBrighter = luminance(fg) > bgLum;

  for (let i = 0; i < 20; i++) {
    if (contrastRatio(current, bg) >= minRatio) return current;
    current = fgIsBrighter ? lighten(current, 0.03) : darken(current, 0.03);
  }

  // Fallback: force to near-white or near-black
  return fgIsBrighter ? '#f0f0f0' : '#111111';
}

// ═══════════════════════════════════════════════════════════════════════════════
// §13 THEME SUMMARY
// ═══════════════════════════════════════════════════════════════════════════════

/** Quick summary for display/debugging. */
export function getTerminalThemeSummary(theme: TerminalTheme): Record<string, string> {
  return {
    background: theme.background,
    foreground: theme.foreground,
    cursor: theme.cursor,
    cursorAccent: theme.cursorAccent,
    selection: theme.selection,
    scrollbar: theme.scrollbar,
    border: theme.border,
    dimOpacity: String(theme.dimOpacity),
    ansiBlack: theme.ansiColors.black,
    ansiRed: theme.ansiColors.red,
    ansiGreen: theme.ansiColors.green,
    ansiYellow: theme.ansiColors.yellow,
    ansiBlue: theme.ansiColors.blue,
    ansiMagenta: theme.ansiColors.magenta,
    ansiCyan: theme.ansiColors.cyan,
    ansiWhite: theme.ansiColors.white,
    glassBlur: `${theme.glass.bufferSurface.blur}px`,
    glassOpacity: String(theme.glass.bufferSurface.opacity),
    glassTint: theme.glass.bufferSurface.tintColor,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §14 MERGE THEME
// ═══════════════════════════════════════════════════════════════════════════════

/** Merge a base theme with partial overrides. */
export function mergeTerminalTheme(base: TerminalTheme, overrides: Partial<TerminalTheme>): TerminalTheme {
  return {
    background: overrides.background ?? base.background,
    foreground: overrides.foreground ?? base.foreground,
    cursor: overrides.cursor ?? base.cursor,
    cursorAccent: overrides.cursorAccent ?? base.cursorAccent,
    selection: overrides.selection ?? base.selection,
    scrollbar: overrides.scrollbar ?? base.scrollbar,
    border: overrides.border ?? base.border,
    ansiColors: overrides.ansiColors
      ? { ...base.ansiColors, ...overrides.ansiColors }
      : base.ansiColors,
    glass: overrides.glass
      ? {
          bufferSurface: { ...base.glass.bufferSurface, ...overrides.glass.bufferSurface },
          scrollbarSurface: { ...base.glass.scrollbarSurface, ...overrides.glass.scrollbarSurface },
          paneBorder: { ...base.glass.paneBorder, ...overrides.glass.paneBorder },
          statusLine: { ...base.glass.statusLine, ...overrides.glass.statusLine },
          tabBar: { ...base.glass.tabBar, ...overrides.glass.tabBar },
          searchOverlay: { ...base.glass.searchOverlay, ...overrides.glass.searchOverlay },
        }
      : base.glass,
    dimOpacity: overrides.dimOpacity ?? base.dimOpacity,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §15 INVERT THEME
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Invert a terminal theme (flip dark ↔ light).
 * Swaps background/foreground, re-derives ANSI palette at inverted lightness,
 * and rebuilds the glass config for the opposite scheme.
 */
export function invertTerminalTheme(theme: TerminalTheme): TerminalTheme {
  const bgLum = luminance(theme.background);
  const wasDark = bgLum < 0.5;
  const newScheme: TerminalColorScheme = wasDark ? 'light' : 'dark';

  // Derive the accent from the cursor (which is the accent in our themes)
  const newTheme = createTerminalTheme(newScheme, theme.cursor);

  // Carry over the cursor color so accent is preserved
  return {
    ...newTheme,
    cursor: theme.cursor,
    cursorAccent: newScheme === 'dark' ? newTheme.background : '#ffffff',
  };
}
